import { Box, Button, Paper } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Movie } from "@mui/icons-material"
import { useSequenzeList } from "../hooks/sequenze"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import useUserCanWriteAndDelete from "../hooks/permissions"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import SequenzeImport from "../components/SequenzeImport"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "numero_sequenza",
})

export default function SequenzeList({ supportoId }: { supportoId?: number }) {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobile()

  const columns: GridColDef[] = useMemo(() => {
    if (isMobile) {
      return [
        {
          field: "filename",
          headerName: t("sequenza.file_digitale"),
          flex: 1,
          renderCell: (params) => {
            return (
              <LangLink
                to={`/files/${params.row.file_digitale}/sequenze?seq=${params.row.id}`}
                style={{ color: "black" }}
              >
                {params.row.filename}
              </LangLink>
            )
          },
        },
      ]
    }
    return [
      {
        field: "filename",
        headerName: t("sequenza.file_digitale"),
        flex: 1,
        renderCell: (params) => {
          return (
            <LangLink
              to={`/files/${params.row.file_digitale}/sequenze?seq=${params.row.id}`}
              style={{ color: "black" }}
            >
              {params.row.filename}
            </LangLink>
          )
        },
      },
      {
        field: "fondo_segnatura",
        headerName: t("sequenza.fondo"),
        flex: 1,
        renderCell: (params) => {
          return (
            <LangLink to={`/fondi/${params.row.fondo_data?.id}`} style={{ color: "black" }}>
              {params.row.fondo_data?.segnatura}
            </LangLink>
          )
        },
      },
      {
        field: "numero_sequenza",
        headerName: t("sequenza.numero_sequenza"),
        flex: 1,
      },
      {
        field: i18n.language === "it" ? "descrizione_it" : "descrizione_en",
        headerName: i18n.language === "it" ? t("sequenza.descrizione_it") : t("sequenza.descrizione_en"),
        flex: 1,
      },
    ]
  }, [t, isMobile])

  const { data, isLoading } = useSequenzeList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    supporto: supportoId ? String(supportoId) : "",
  })

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const [importModalState, importModalActions] = useModalTrigger()

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {!supportoId && (
            <PageTitle
              title={t("sequenza.titolo_lista")}
              breadcrumbs={[
                {
                  label: t("sequenza.sequenze"),
                  iconType: Movie,
                },
              ]}
            />
          )}
          {userCanWriteAndDelete && !isMobile && !supportoId && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                importModalActions.open()
              }}
              sx={{
                marginRight: 1,
              }}
            >
              {t("sequenza.cta_importa")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1 })}
              ref={toolbarRef}
            />
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <SequenzeImport open={importModalState.isOpen} onClose={importModalActions.close} />
    </Layout>
  )
}
